import * as React from 'react';
import { Navigate } from 'react-router-dom';

import DocumentTitle from '@Components/DocumentTitle';
import AdFilters, { AdFilterFields } from '@Components/forms/Filter/AdFilters';
import Container from '@Components/layout/Container';
import Layout, { LayoutBackgrounds } from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import LoadingSpinner from '@Components/LoadingSpinner';
import config from '@Config/config';
import { FETCH_LIMIT } from '@Config/constants';
import { RouteConfig } from '@Config/routes';
import { useGetCompanyBySlugQuery } from '@Graphql/graphqlTypes.generated';
import { useRouteParams } from '@Hooks/useRouteParams';
import { hasData } from '@Utils/graphql';
import { useAdvertFilterData } from '@Utils/hooks/useAdvertFilterData';
import { convertMoneyToCents } from '@Utils/money';
import { defaultSearchValues } from '@Utils/types';

import CompanyAdsList from './CompanyAdsList/CompanyAdsList';
import CompanyInfoContainer from './CompanyInfoContainer/CompanyInfoContainer';
import styles from './CompanyPage.module.scss';
import CompanyReviewsList from './CompanyReviewsList';

export const defaultFiltersValues: AdFilterFields = {
  priceGte: undefined,
  priceLte: undefined,
  advertType: undefined,
  categories: undefined,
  searchBy: undefined,
};

const CompanyPage: React.FunctionComponent<{}> = () => {
  const { slug } = useRouteParams(RouteConfig.Company);

  const { data, loading, error } = useGetCompanyBySlugQuery({
    variables: { slug },
    fetchPolicy: 'network-only',
  });

  const {
    filterData,
    sortData,
    geoSearchWithDistance,
    searchData,
    filtersInitialValues,
    defaultQuerySortValues,
    onFiltersClear,
    onFiltersChange,
  } = useAdvertFilterData();

  if (config.DISABLE_COMPANY_PAGE) {
    return <Navigate to={RouteConfig.NotFound.buildLink()} replace />;
  }
  if (error || !hasData(data, loading, 'companyBySlug')) {
    return <Navigate to={RouteConfig.NotFound.buildLink()} replace />;
  }

  const loadableContent = () => {
    if (!data || loading) {
      return <LoadingSpinner />;
    }

    const variables = {
      limit: FETCH_LIMIT,
      ...defaultFiltersValues,
      ...defaultSearchValues,
      ...defaultQuerySortValues,
      ...geoSearchWithDistance,
      ...searchData,
      company: data.companyBySlug.id,
      priceGte: convertMoneyToCents(Number(filterData.priceGte)),
      priceLte: convertMoneyToCents(Number(filterData.priceLte)),
      ...sortData,
    };

    return (
      <Container>
        <Row constant size={12} className={styles.header}>
          <CompanyInfoContainer companyInfo={data.companyBySlug} />
        </Row>
        <Row>
          <AdFilters
            onClick={onFiltersClear}
            onChange={onFiltersChange}
            initialValues={filtersInitialValues}
            showDistanceField={filterData.geoSearch}
            submitOnChange
          />
        </Row>
        <Row constant size={12} className={styles.header}>
          <CompanyAdsList variables={variables} companyId={data.companyBySlug.id} />
        </Row>
        <Row constant size={12} className={styles.header}>
          <CompanyReviewsList companyId={data.companyBySlug.id} />
        </Row>
      </Container>
    );
  };

  return (
    <DocumentTitle title={data?.companyBySlug.name}>
      <Layout background={LayoutBackgrounds.grayBackground} className={styles.companyPageContainer}>
        {loadableContent()}
      </Layout>
    </DocumentTitle>
  );
};

export default CompanyPage;
